/**
 * Central secret-store client (see access_api_usage.md).
 *
 * Secrets (the OpenAI key, the AWS credentials) are NO LONGER bundled into the build. They are
 * fetched at runtime from a password-gated Lambda endpoint: you POST `{ key, password }` and get
 * back `{ key, value }`, where `value` is a plain string for a single secret or a nested object
 * for a structured one. The `key` is hardcoded per secret; the `password` is the user's and is
 * entered once at startup (held in `sessionStorage`, cleared when the tab closes — never on disk).
 *
 * A `401` deliberately does not distinguish a wrong key from a wrong password, so callers must not
 * branch on which was wrong — only on "unauthorized vs not".
 */

export const AUTH_URL = "https://k323ld5jq5el2ebqqychsimjem0tlhwl.lambda-url.ap-south-1.on.aws/";

/** Hardcoded secret names (access_api_usage.md "Key and Secrets used in this"). */
export const OPENAI_SECRET_KEY = "btr_oai";
export const AWS_SECRET_KEY = "btr_aws";

/** sessionStorage key for the user-entered access password. */
const PASSWORD_STORAGE_KEY = "btr_access_password";

/** Thrown on a 401 — wrong key or password (indistinguishable by design). */
export class AccessUnauthorizedError extends Error {
  constructor() {
    super("Wrong password");
    this.name = "AccessUnauthorizedError";
  }
}

function _session(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null; // sessionStorage can throw in sandboxed/SSR contexts
  }
}

/** Store the access password for the rest of this tab session. */
export function set_access_password(password: string): void {
  _session()?.setItem(PASSWORD_STORAGE_KEY, password);
}

/** The stored access password, or null if none has been entered this session. */
export function get_access_password(): string | null {
  const pw = _session()?.getItem(PASSWORD_STORAGE_KEY);
  return pw && pw.length ? pw : null;
}

/** Forget the stored password (e.g. after it proved wrong). */
export function clear_access_password(): void {
  _session()?.removeItem(PASSWORD_STORAGE_KEY);
}

/**
 * URL-hash suffixes that, appended AFTER the access password, request a special mode instead of
 * the normal app. The suffix is NOT part of the password; it is stripped before the password is
 * sent to the Access API. Two modes exist:
 *
 *  - `admin` — open the admin (user-management) page. `#<password>_admin` or `#<password>+admin`.
 *  - `all`   — stay in the normal app but unlock EVERY saved profile (bypass the local-only roster
 *              gate), so any person on DynamoDB can be opened. `#<password>+all`.
 *
 * Each mode lists its accepted suffixes; the first entry is the canonical one.
 */
export const ADMIN_HASH_SUFFIXES = ["_admin", "+admin"] as const;
export const UNLOCK_ALL_HASH_SUFFIXES = ["+all"] as const;

/** Canonical admin suffix (kept for backwards compatibility with existing links/tests). */
export const ADMIN_HASH_SUFFIX = ADMIN_HASH_SUFFIXES[0];

/** Every mode suffix, longest-first, so `_matched_mode_suffix` strips greedily. */
const _ALL_MODE_SUFFIXES: readonly string[] = [...ADMIN_HASH_SUFFIXES, ...UNLOCK_ALL_HASH_SUFFIXES].sort(
  (a, b) => b.length - a.length,
);

/** The mode suffix present at the end of `raw` (from any mode), or null if none is. */
function _matched_mode_suffix(raw: string): string | null {
  return _ALL_MODE_SUFFIXES.find((s) => raw.endsWith(s)) ?? null;
}

/** True when `raw` ends with one of `suffixes` AND has a non-empty password before it. */
function _has_mode(raw: string | null, suffixes: readonly string[]): boolean {
  return raw != null && suffixes.some((s) => raw.endsWith(s) && raw.length > s.length);
}

/** The raw hash fragment text (decoded, trimmed), or null when there is no usable fragment. */
function _raw_url_fragment(): string | null {
  try {
    const hash = typeof location !== "undefined" ? location.hash : "";
    if (!hash || hash.length <= 1) return null;
    const raw = decodeURIComponent(hash.slice(1)).trim(); // drop the leading '#'
    return raw.length ? raw : null;
  } catch {
    return null; // malformed %-escape, or no `location`
  }
}

/**
 * A password supplied directly in the URL hash fragment, e.g. `https://…/#thisisapassword` →
 * `"thisisapassword"`. The fragment is never sent to any server (it stays client-side), which is
 * what makes carrying the password here safe-ish; it lets a bookmarked link auto-unlock the app.
 * A trailing `_admin` (the admin-page suffix) is stripped — it requests a page, not part of the
 * password. Returns null when there is no usable fragment.
 */
export function get_url_password(): string | null {
  const raw = _raw_url_fragment();
  if (!raw) return null;
  const suffix = _matched_mode_suffix(raw);
  const pw = suffix ? raw.slice(0, -suffix.length) : raw;
  return pw.length ? pw : null;
}

/**
 * True when the URL hash requests the admin page — i.e. the fragment is `<password>_admin` (or the
 * `+admin` alias). A bare `#_admin` (no password before the suffix) does NOT count, so the suffix
 * alone can't open it.
 */
export function is_admin_url(): boolean {
  return _has_mode(_raw_url_fragment(), ADMIN_HASH_SUFFIXES);
}

/**
 * True when the URL hash requests "unlock all profiles" — i.e. the fragment is `<password>+all`.
 * This keeps the normal app but bypasses the local-only roster gate so EVERY saved profile can be
 * opened. A bare `#+all` (no password before the suffix) does NOT count.
 */
export function is_unlock_all_url(): boolean {
  return _has_mode(_raw_url_fragment(), UNLOCK_ALL_HASH_SUFFIXES);
}

/**
 * The password to try at startup, in priority order: the URL hash (an explicit auto-unlock link)
 * over a password already stored from this tab session.
 */
export function resolve_initial_password(): string | null {
  return get_url_password() ?? get_access_password();
}

// ── Fetch seam (tests stub this) ────────────────────────────────────────────────────────────

type FetchImpl = (
  url: string,
  init: Record<string, any>,
) => Promise<{ ok: boolean; status: number; json(): Promise<any> }>;

let _fetch_impl: FetchImpl | null = null;

export function set_access_fetch_impl(impl: FetchImpl | null): void {
  _fetch_impl = impl;
}

function _fetch(): FetchImpl {
  if (_fetch_impl) return _fetch_impl;
  if (typeof fetch === "undefined") throw new Error("fetch is unavailable in this environment");
  return fetch as unknown as FetchImpl;
}

/**
 * Fetch one secret's value by key, proving authorization with `password`. Returns the raw value
 * (string for a single secret, object for a structured one). Throws `AccessUnauthorizedError` on
 * 401; throws a generic error on any other failure. Never logs the body or the value.
 */
export async function fetch_secret(key: string, password: string): Promise<string | Record<string, any>> {
  const res = await _fetch()(AUTH_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, password }),
  });
  if (res.status === 401) throw new AccessUnauthorizedError();
  if (!res.ok) throw new Error(`Access API request for "${key}" failed (${res.status})`);
  const body = await res.json();
  if (body == null || !("value" in body)) throw new Error(`Access API returned no value for "${key}"`);
  return body.value;
}
